import { useCallback, useSyncExternalStore } from 'react'
import { supabase } from '../lib/supabase'
import {
  collectionKeyFor,
  getActiveCollectionKey,
  nextEpoch,
  onActiveCollectionKeyChange,
  ownerOfCollectionKey,
  publishCollectionScope,
} from '../lib/collectionScope'
import { applyCollectionScopeFromAuth } from '../lib/collectionScopeBinding'
import { DURABLE_REMOVAL } from '../lib/collectionMigration.codes'
import {
  EMPTY_COLLECTION as EMPTY,
  livePreviousCollection,
  readCollectionRecord,
  removeCollectionRecord,
  removeCollectionRecordDurable,
  writeCollectionRecord,
  type PendingUrlAction,
  type PreviousCollection,
  type StoredCollection,
} from '../lib/collectionCache'
import {
  beginCollectionWrite, bumpCollectionClearGeneration, consumeOwnCopiesChanges, noteCollectionIdentityChange, pointerLeaseActive,
  settleCopiesWritesForRemoval, type CopiesSettle,
} from '../lib/collectionWriteFence'
import { clearRetryIntents } from '../lib/copiesRetryIntents'
import { clearAllPendingPulls } from '../lib/sealedPendingPulls'
import { pointerRemovalInProgress, pointerWriteSeq, removeExclusively, writePointer } from '../lib/collectionPointerWriter'
import { readSessionIdentity } from '../lib/accessToken'
import { isCollectionLeader } from '../lib/collectionLeader'
import { clearSavedCollectionPointer, upsertSavedCollection, type SavedCollectionRow } from '../lib/savedCollections'
import { getSupabaseIdentity } from './useSupabaseToken'
import { CLOUD_DELETE, type CloudDeleteOutcome } from './collectionRemoval.codes'
import { JOURNAL, type JournalResult } from './urlActionJournal.codes'
import { clearLegacyCollectionMetadataAs, readLegacyCollectionIdAs, supabaseForToken } from '../lib/supabaseScoped'

export type { PreviousCollection, StoredCollection } from '../lib/collectionCache'

// Result of a saveCollection call. `cloudOk` reports whether the cloud pointer
// upsert (saved_collections) succeeded. Local-first callers can ignore it (local
// state is always written first); cross-device plumbing (claim-on-login) uses it
// to avoid masking a failed sync behind a "done" marker. Local-only / signed-out
// (no cloud pointer to update) reports cloudOk:true - nothing could have failed.
//
// `localOk` reports whether the localStorage write itself landed. A quota
// failure must NOT be read as a committed sync: the owned-name sync only
// records a version after a confirmed local write, or a device could certify a
// version over names it never stored.
/**
 * What the user CONFIRMED: whose collection (null = signed out, this device
 * only) and which one. Captured when the Remove button in the dialog is
 * pressed; the removal acts on exactly this and refuses anything else.
 */
export interface RemovalIntent {
  userId: string | null
  collectionId: string | null
  /** The collection was confirmed to be in that account (cloudSynced). */
  inAccount: boolean
}

export interface ClearCollectionResult {
  ok: boolean
  /**
   * Set with ok:false:
   *  - local_clear_failed: the ACCOUNT copy was removed but this browser could
   *    not durably clear its own copy (storage refused). The cloud side stays
   *    done; memory is left as it was so the user can retry.
   *  - identity_changed: the signed-in account is no longer the one that
   *    confirmed (before anything was sent, or after its delete). NOT
   *    retryable: a retry would act for a different account.
   *  - collection_changed: the account's cloud collection is not the one that
   *    was confirmed (replaced on another device). Nothing was deleted; not
   *    retryable.
   *  - incomplete: a delete was attempted for the confirming account, but the
   *    account changed before the removal was confirmed (row read back absent
   *    AND legacy metadata cleared). The account copy may survive. Retryable,
   *    but ONLY as the confirming account.
   *  - unconfirmed: the same account, but whether its legacy metadata pointer
   *    is this collection could not be read (network). Nothing changed;
   *    retryable.
   *  - syncing: a pointer mover (the sign-in reconcile, the legacy migration,
   *    "Use account collection") holds the pointer lease. Nothing was started;
   *    retryable once it ends.
   * Absent: a retryable failure (network, verification), nothing local cleared.
   */
  reason?: 'local_clear_failed' | 'identity_changed' | 'collection_changed' | 'incomplete' | 'unconfirmed' | 'syncing'
}

export interface SaveCollectionResult {
  cloudOk: boolean
  localOk: boolean
}

interface CollectionState {
  collectionId: string | null
  ownedCards: string[] | null
  cardCount: number | null
  sourceUrl: string | null
  /**
   * The server `content_hash` the cached names were fetched at, or null.
   *
   * null means UNVERSIONED - a client parse that has not been reconciled, or a
   * legacy row that has no hash. Either way the next sync refetches. It NEVER
   * means "matches the server's null".
   */
  version: string | null
  /**
   * WHICH collection `ownedCards` came from.
   *
   * Distinct from `collectionId`, which is the CURRENT pointer, because a
   * pointer-only save (the copy-on-write claim adopting a new id) moves the
   * pointer while leaving the old names in place. Without this field those
   * names would silently be relabelled as the new collection's and reported as
   * owned for a collection they were never in.
   */
  ownedCardsCollectionId: string | null
  /**
   * The C4 ownership snapshot (plan 2026-10-16): the names held ONLY through
   * proxies, from the same owned-names walk (same version) as `ownedCards`,
   * and bound to the same collection AND user. Empty unless a canonical sync
   * for that user committed it; never shown unless the per-copy capability is
   * per_copy (read it through useProxyNames).
   */
  proxyNames: string[]
  /** The owned-names walk said copies_unavailable (a diverged row). */
  copiesUnavailable: boolean
  /** The user the names were synced for (null: a local parse or a signed-out sync). */
  ownedCardsUserId: string | null
  /**
   * Provenance of `proxyNames`: true only when the walk that produced them saw
   * proxy_names on EVERY page. False means "unknown" (certified while the
   * server was dark or the pair was not per_copy), and CollectionSync forces
   * one full walk past the version shortcut once the pair is per_copy.
   */
  proxyNamesKnown: boolean
  /**
   * The collection a choice replaced on this account, offered for 30 days as
   * "Switch back" (null for a guest, or none / expired).
   */
  previousCollection: PreviousCollection | null
  /**
   * Monotonic stamp of the last content-changing save (a new pointer or a new
   * owned list). null means the stored blob predates the field (a pre-printings
   * client) or nothing is stored. See `src/lib/collectionScope.ts`.
   */
  epoch: number | null
  /**
   * True when the signed-in user's saved_collections row is known to point at
   * the CURRENT collection: the last pointer upsert succeeded, or the login
   * read found the row. False signed out, before either has happened, or after
   * a failed upsert - the collection then lives on this device only.
   */
  cloudSynced: boolean
  loading: boolean
  saveCollection: (data: SaveCollectionInput) => Promise<SaveCollectionResult>
  /**
   * Remove the collection. Signed in, the account copy is deleted FIRST and
   * verified gone; only then is this device cleared. `ok: false` means the
   * account copy may still exist and NOTHING local was cleared, so the caller
   * can say so and let the user retry. Signed out, it clears this device.
   */
  clearCollection: (intent: RemovalIntent) => Promise<ClearCollectionResult>
}

export interface SaveCollectionInput {
  collectionId?: string
  ownedCards?: string[]
  sourceUrl?: string
  cardCount?: number
  /** Only a caller that FETCHED the canonical set passes this. */
  version?: string | null
  /**
   * A canonical commit only: collectionWriteFence.ownCopiesChangeStamp() taken
   * BEFORE the names were read. A version change this tab's own confirmed
   * per-copy writes (before the stamp) cannot explain is a REMOTE replacement
   * of the same collection id, and moves its identity.
   */
  syncStamp?: number
  /**
   * Where this write came from. MUST be explicit for a canonical commit.
   *
   * This was derived from `version === null` and that was wrong: the server
   * may legitimately return a canonical snapshot with a null version (a corpus
   * it declines to certify), which the derivation then misread as an import -
   * so the sync invalidated its own result, re-keyed, and re-downloaded the
   * collection once per loop iteration.
   *
   * Absent defaults to "import" when names are written, which is the SAFE
   * direction: a forgotten flag causes one extra resync, never a missed one.
   */
  origin?: 'import' | 'canonical'
  /** Defaults to the collection the names were written alongside. */
  ownedCardsCollectionId?: string | null
  /**
   * C4: the proxy-only names of the SAME snapshot as ownedCards. Only read
   * when ownedCards is written; absent = none (an import's client parse has
   * no proxy knowledge, the next canonical sync fills it).
   */
  proxyNames?: string[]
  copiesUnavailable?: boolean
  /** The user the snapshot was fetched for (absent = none). */
  ownedCardsUserId?: string | null
  /** The walk saw proxy_names on every page (absent = unknown). */
  proxyNamesKnown?: boolean
  /**
   * Force the epoch to stamp even when the pointer and owned-name list both
   * compare equal to what is already stored - a printings-only re-import
   * (same names, same collection id, only the owned-printing identifiers
   * changed) is exactly that case, and the string-list comparison used to
   * decide `pointerChanged`/`listChanged` cannot see a printing-only diff.
   * Every import completion passes this; CollectionSync's unversioned resync
   * deliberately does not, so a benign resync still cannot self-invalidate
   * every gallery keyed on the epoch.
   */
  contentChanged?: boolean
  /**
   * collectionPointerWriter sequence captured when the OPERATION started.
   * Defaults to "now", which is right for a caller that checked its write
   * fence in the same tick. An operation that awaits a server call BEFORE it
   * saves (CollectionSync's claim) passes the number it captured up front, so
   * a Remove that succeeded meanwhile rejects its cloud write.
   */
  pointerSeq?: number
}

const COPIES_TIMED_OUT: CopiesSettle = 'timed_out'

// The ACTIVE scope's record (collectionScope.ts: the signed-in account's key,
// or the guest key). Nothing is read or written while the scope is unknown.
function loadLocal(): StoredCollection {
  return readCollectionRecord(getActiveCollectionKey())
}

/**
 * Record invariants, applied to EVERY write of the store (setState and the
 * durable-first writes): the "Switch back" slot never names the active
 * collection (adopting a row, a save or a swap that lands on the slot's
 * collection retires it), and the 403 provenance exists only on a pending
 * null pointer.
 */
function withInvariants(r: StoredCollection): StoredCollection {
  let next = r
  if (next.previousCollection && next.previousCollection.collectionId === next.collectionId) {
    next = { ...next, previousCollection: null }
  }
  if (next.pendingNullOf !== null && (!next.pointerPending || next.collectionId !== null)) {
    next = { ...next, pendingNullOf: null }
  }
  return next
}

// Quota exceeded, private-mode restrictions, disabled storage, or no scope yet:
// reported rather than thrown. Callers stay working off in-memory state, and
// the owned-name sync must not record a version it did not durably store.
function persistLocal(data: StoredCollection): boolean {
  return writeCollectionRecord(getActiveCollectionKey(), withInvariants(data))
}

// Null-safe, order-sensitive content comparison for the owned list. Used to
// decide whether the epoch should move: a re-fetch that returns the exact
// same names (the common case for an UNVERSIONED canonical resync, or a
// second useCollection instance hydrating the same cloud row) must not bump
// it, or every gallery keyed on the epoch (Task 7) refetches for nothing.
function sameNames(a: string[] | null, b: string[] | null): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

// Stamp only when the hydrated pointer OR owned list differs from what this
// tab already has. Several useCollection instances hydrate the same row on
// one page load; comparing against the freshly read blob (not a
// render-captured snapshot) keeps the second and later hydrations from
// re-stamping, and persisting an unchanged blob is a no-op for the store
// (same raw text, same snapshot).
//
// The kept/new epoch is derived from the LARGER of the latest-persisted and
// this instance's in-memory (`base`) epoch, not just the persisted one: a
// prior save whose localStorage write threw (quota, private mode) still
// advances `state` in memory without persisting, so `loadLocal()` alone can
// under-report the true epoch and a later hydration would otherwise regress
// it on disk.
function hydrated(base: StoredCollection, next: Omit<StoredCollection, 'epoch'>): StoredCollection {
  const latest = loadLocal()
  const changed = latest.collectionId !== next.collectionId
    || latest.cardCount !== next.cardCount
    || latest.sourceUrl !== next.sourceUrl
    || !sameNames(latest.ownedCards, next.ownedCards)
  const epochFloor = Math.max(latest.epoch ?? 0, base.epoch ?? 0)
  return { ...next, epoch: changed ? nextEpoch(epochFloor) : epochFloor }
}

// --- Module-level store -----------------------------------------------------
//
// Every useCollection() call USED to own independent React state, which meant a
// write from one component (an import on CollectionPage) was invisible to
// another mounted component (CollectionSync) until a full reload, and
// same-window localStorage writes fire no `storage` event. That is what let a
// re-import sit unreconciled. One shared store fixes both, and gives
// CollectionSync a revision to re-run on.

// TWO COUNTERS, deliberately, because "the cache changed" and "the cache needs
// revalidating" are different questions and conflating them made the sync
// re-trigger itself.
//
//  - `revision` moves on ANY change. Consumers re-render off it.
//  - `syncGeneration` moves ONLY on an INVALIDATION - an import replacing the
//    names, or a clear. A canonical sync commit (names + a real version) does
//    NOT move it, so the sync cannot invalidate its own result, spin, or
//    consume its own retry budget.
let state: StoredCollection = loadLocal()
let revision = 0
let syncGeneration = 0
// Moves only when the POINTER or its metadata changes (or on a clear). The
// cloud-pointer read guards on this: guarding on `revision` let a benign
// names-only commit discard the pointer response, and since that read only
// re-runs on a user change, the stale pointer stuck for the whole session.
let pointerGeneration = 0
const listeners = new Set<() => void>()

function emit() {
  revision += 1
  for (const l of listeners) l()
}

function emitInvalidation() {
  syncGeneration += 1
  emit()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

// Referential stability is required by useSyncExternalStore: `state` is only
// ever REPLACED on a write, never mutated, so repeated reads between writes
// return the identical object and React does not loop.
function getSnapshot(): StoredCollection {
  return state
}

function getRevision(): number {
  return revision
}

function getSyncGeneration(): number {
  return syncGeneration
}

// Which (user, collection) the cloud pointer row is CONFIRMED to hold. Its own
// listeners, not emit(): this is display state, and bumping `revision` would
// re-run CollectionSync for nothing. Memory only - a reload re-learns it from
// the login read of saved_collections.
type CloudPointer = { userId: string; collectionId: string | null }
let cloudPointer: CloudPointer | null = null
const cloudListeners = new Set<() => void>()

function setCloudPointer(next: CloudPointer | null) {
  if (next?.userId === cloudPointer?.userId && next?.collectionId === cloudPointer?.collectionId) return
  cloudPointer = next
  for (const l of cloudListeners) l()
}

function subscribeCloud(cb: () => void): () => void {
  cloudListeners.add(cb)
  return () => { cloudListeners.delete(cb) }
}

function getCloudPointer(): CloudPointer | null {
  return cloudPointer
}

/**
 * `identityChanged`: the collection a per-copy write may target is no longer
 * the same one even under the same id (its contents were replaced by an
 * import or an adoption). A new id always counts. A canonical names commit
 * never passes it (collectionWriteFence.noteCollectionIdentityChange).
 */
function setState(input: StoredCollection, invalidates = false, identityChanged = false): boolean {
  const next = withInvariants(input)
  if (identityChanged || next.collectionId !== state.collectionId) noteCollectionIdentityChange()
  if (next.collectionId !== state.collectionId
      || next.cardCount !== state.cardCount
      || next.sourceUrl !== state.sourceUrl) {
    pointerGeneration += 1
  }
  const ok = persistLocal(next)
  // In-memory state is updated regardless (local-first: the session keeps
  // working even when storage refuses the write), but `ok` tells the caller
  // whether it survived the tab.
  state = next
  // The scope store is told the IN-MEMORY pointer and epoch, not "go re-read
  // storage", and is told it whether or not `ok`. A same-window localStorage
  // write fires no native `storage` event, so this publish is the only way
  // same-tab readers hear about the write at all - and on a refused write a
  // re-read would hand every gallery the PREVIOUS collection while this tab
  // works off the new one.
  publishCollectionScope({ collectionId: next.collectionId, epoch: next.epoch ?? 0 })
  if (invalidates) emitInvalidation()
  else emit()
  return ok
}

/**
 * Re-read the active scope's record into memory and tell every reader. For
 * writers outside this module (the leader's migration and sign-in reconcile)
 * after they wrote the active key, and for the storage event below.
 */
export function reloadActiveCollection(): void {
  const before = state
  state = loadLocal()
  if (state.collectionId !== before.collectionId || (state.epoch ?? 0) !== (before.epoch ?? 0)) noteCollectionIdentityChange()
  notifyJournal()
  pointerGeneration += 1
  publishCollectionScope({ collectionId: state.collectionId, epoch: state.epoch ?? 0 })
  emitInvalidation()
}

// --- Scope switch -----------------------------------------------------------
//
// The active key follows the shared auth store (collectionScopeBinding). A
// switch replaces the in-memory record wholesale, drops the confirmed cloud
// pointer, and fences any write still in flight from the previous scope.

/** The collection owner of the active scope (null = guest or unknown). */
export function activeCollectionOwner(): string | null {
  return ownerOfCollectionKey(getActiveCollectionKey())
}

onActiveCollectionKeyChange(() => {
  bumpCollectionClearGeneration()
  setCloudPointer(null)
  reloadActiveCollection()
})

if (typeof window !== 'undefined') {
  // Cross-TAB writes. Only the leader tab writes (collectionLeader), so this is
  // how every other tab picks its changes up. Same-window writes go through
  // setState above.
  window.addEventListener('storage', (e) => {
    const key = getActiveCollectionKey()
    if (key === null || (e.key !== null && e.key !== key)) return
    const before = state
    // A removal in the writer tab fences this tab's view too.
    const hadCollection = before.collectionId != null || (before.ownedCards?.length ?? 0) > 0
    const next = loadLocal()
    const hasCollection = next.collectionId != null || (next.ownedCards?.length ?? 0) > 0
    if (hadCollection && !hasCollection) bumpCollectionClearGeneration()
    reloadActiveCollection()
  })
}

/**
 * Point the ACTIVE account scope at its saved_collections row, or at the
 * legacy user_metadata pointer when it has no row. Writer tab only, and only
 * while `ownerId` is still the active scope (a switch meanwhile drops it).
 *
 * Pointer-only: names deliberately do not live in the cloud row, and adopting
 * a different id makes the cached names stop counting (they stay bound to the
 * collection they came from) until the owned-name sync fetches the new set.
 *
 * True when it was adopted (or there was nothing to adopt); false only when
 * it was refused (not the writer tab, not the active owner). Local-first: an
 * adoption whose localStorage write failed (quota, blocked storage) still
 * holds in memory for this session, exactly as before, and is NOT a failure
 * the caller should retry forever.
 */
export function adoptAccountRow(
  ownerId: string,
  row: SavedCollectionRow | null,
  meta?: Record<string, unknown> | null,
): boolean {
  if (!isCollectionLeader() || activeCollectionOwner() !== ownerId) return false
  if (row) {
    // hydrated() stamps the epoch only when this differs from what is stored.
    setState(hydrated(state, {
      collectionId: row.collection_id ?? null,
      ownedCards: state.ownedCards,
      cardCount: row.card_count ?? null,
      sourceUrl: row.source_url ?? null,
      version: state.version,
      ownedCardsCollectionId: state.ownedCardsCollectionId,
      proxyNames: state.proxyNames,
      copiesUnavailable: state.copiesUnavailable,
      ownedCardsUserId: state.ownedCardsUserId,
      proxyNamesKnown: state.proxyNamesKnown,
      // The row IS the cloud pointer: nothing local is waiting to sync.
      pointerPending: false,
      previousCollection: state.previousCollection,
      pendingMint: null,
      pendingClaim: null,
      pendingUrlActions: state.pendingUrlActions,
      pendingNullOf: null,
    }))
    // The row just read IS the cloud copy of this pointer.
    setCloudPointer({ userId: ownerId, collectionId: row.collection_id ?? null })
    return true
  }
  // Legacy user_metadata migration. owned_cards MUST come across: a pointer
  // with no names leaves every deck card reading as unowned until a sync
  // lands. Bound to the metadata collection and left UNVERSIONED so the sync
  // still replaces it with the canonical set.
  const metaId = typeof meta?.collection_id === 'string' && meta.collection_id ? meta.collection_id : null
  if (!metaId) return true
  const legacyNames = Array.isArray(meta?.owned_cards) ? (meta.owned_cards as string[]) : null
  setState(hydrated(state, {
    collectionId: metaId,
    ownedCards: legacyNames ?? state.ownedCards,
    cardCount: typeof meta?.collection_card_count === 'number' ? meta.collection_card_count : null,
    sourceUrl: typeof meta?.collection_source_url === 'string' ? meta.collection_source_url : null,
    version: legacyNames ? null : state.version,
    ownedCardsCollectionId: legacyNames ? metaId : state.ownedCardsCollectionId,
    proxyNames: legacyNames ? [] : state.proxyNames,
    copiesUnavailable: legacyNames ? false : state.copiesUnavailable,
    ownedCardsUserId: legacyNames ? null : state.ownedCardsUserId,
    proxyNamesKnown: legacyNames ? false : state.proxyNamesKnown,
    pointerPending: false,
    previousCollection: state.previousCollection,
    pendingMint: null,
    pendingClaim: null,
    pendingUrlActions: state.pendingUrlActions,
    pendingNullOf: null,
  }))
  return true
}

/**
 * Make `record` (this device's guest collection, possibly under a new id from
 * a copied claim) the ACTIVE account scope's collection. Writer tab only, and
 * only while `ownerId` is still active. True when it was written durably.
 */
export function adoptIntoAccount(
  ownerId: string,
  record: StoredCollection,
  opts: { previous?: Omit<PreviousCollection, 'at'> | null } = {},
): boolean {
  if (!isCollectionLeader() || activeCollectionOwner() !== ownerId) return false
  const epochFloor = Math.max(state.epoch ?? 0, record.epoch ?? 0)
  const changed = record.collectionId !== state.collectionId || !sameNames(record.ownedCards, state.ownedCards)
  return setState({
    ...record,
    // Pending until the upsert that follows is confirmed (confirmAccountPointer).
    pointerPending: record.collectionId !== null,
    // The collection this replaced, when the user chose it away: the way back.
    previousCollection: opts.previous ? { ...opts.previous, at: Date.now() } : state.previousCollection,
    pendingMint: null,
    pendingClaim: null,
    pendingUrlActions: state.pendingUrlActions,
    pendingNullOf: null,
    epoch: changed ? nextEpoch(epochFloor) : epochFloor,
  }, true, changed)
}

/**
 * Record that `ownerId`'s saved_collections row is known to hold `collectionId`,
 * and that the account record's pointer (when it is that collection) is no
 * longer waiting to sync.
 */
export function confirmAccountPointer(ownerId: string, collectionId: string | null): void {
  if (activeCollectionOwner() !== ownerId) return
  setCloudPointer({ userId: ownerId, collectionId })
  if (isCollectionLeader() && state.pointerPending && state.collectionId === collectionId) {
    setState({ ...state, pointerPending: false })
  }
}

/**
 * Keep `previous` as the account's way back (the collection a choice did not
 * keep). Writer tab, active account only. True when it was written durably.
 */
export function rememberPreviousCollection(ownerId: string, previous: Omit<PreviousCollection, 'at'>): boolean {
  if (!isCollectionLeader() || activeCollectionOwner() !== ownerId) return false
  return setState({ ...state, previousCollection: { ...previous, at: Date.now() } })
}

/** Forget the way back (the user dismissed it). */
export function dismissPreviousCollection(ownerId: string): void {
  if (!isCollectionLeader() || activeCollectionOwner() !== ownerId || !state.previousCollection) return
  setState({ ...state, previousCollection: null })
}

/**
 * Switch the account back to its previous collection: the two swap places
 * (so the switch itself can be undone the same way) and the account's
 * saved_collections row is pointed at it. The cached names stay bound to the
 * collection they came from, so they stop counting until the owned-name sync
 * fetches the switched-to set. True when the account row now holds it.
 */
export async function switchToPreviousCollection(ownerId: string): Promise<boolean> {
  if (!isCollectionLeader() || activeCollectionOwner() !== ownerId) return false
  // Never past a Remove: its tombstone would refuse the upsert below and its
  // clear would then wipe the switched-to pointer.
  if (pointerRemovalInProgress()) return false
  const prev = livePreviousCollection(state)
  if (!prev) return false
  // In flight for Remove (its button is disabled) and fenced like an import.
  const write = beginCollectionWrite()
  if (!write || write.userId !== ownerId) { write?.end(); return false }
  try {
    const current = state.collectionId
      ? { collectionId: state.collectionId, cardCount: state.cardCount, sourceUrl: state.sourceUrl, at: Date.now() }
      : null
    const next: StoredCollection = {
      ...state,
      collectionId: prev.collectionId,
      cardCount: prev.cardCount,
      sourceUrl: prev.sourceUrl,
      version: null,
      pointerPending: true,
      previousCollection: current,
      epoch: nextEpoch(state.epoch ?? 0),
    }
    // DURABLE FIRST: the swap (with the displaced pointer in the slot) must
    // survive a reload before the account row moves, or the row could point
    // at the switched-to collection while the displaced one was never saved.
    // Refused: no state change and no cloud write.
    if (!persistLocal(next)) return false
    setState(next, true)
    const up = await writePointer(ownerId, write.pointerSeq, () => upsertSavedCollection(ownerId, {
      collection_id: prev.collectionId,
      card_count: prev.cardCount,
      source_url: prev.sourceUrl,
    }))
    // Not confirmed: the swap stays local and pending, and the next load's
    // reconcile retries the upsert (a pending pointer wins over the row).
    if (!up.ok || !up.value) return false
    confirmAccountPointer(ownerId, prev.collectionId)
    return true
  } finally {
    write.end()
  }
}

// --- URL action journal (API spec 5.1 op_key) --------------------------------
//
// The unsettled URL imports/refreshes of the ACTIVE scope, on its own record,
// so each op_key survives a reload. Writer tab only, and only for the scope's
// own owner (null = the guest scope). None of these move the pointer
// generation, publish the scope or bump the revision: the journal has its own
// listeners.

/** At most this many unsettled URL actions per scope; a new one past it is refused. */
export const URL_ACTION_CAP = 8
/**
 * An unsettled entry older than this is abandoned: dropped, and the user told
 * the earlier import may have completed. Well inside what the API can still
 * replay (a stream key is stamped on the row it wrote and answers until the
 * next write to that row; the per-copy ledger keeps keys 30 days).
 */
export const URL_ACTION_TTL_MS = 24 * 60 * 60 * 1000

const journalListeners = new Set<() => void>()
function notifyJournal() {
  for (const l of journalListeners) l()
}
function subscribeJournal(cb: () => void): () => void {
  journalListeners.add(cb)
  return () => { journalListeners.delete(cb) }
}
const EMPTY_ACTIONS: readonly PendingUrlAction[] = []
function getJournal(): readonly PendingUrlAction[] {
  return state.pendingUrlActions ?? EMPTY_ACTIONS
}

// Entries abandoned in THIS session (expired, or no longer resumable), for
// the "may have completed" notice. Memory only; a scope switch clears it.
let abandoned: readonly PendingUrlAction[] = []
const abandonedListeners = new Set<() => void>()
function setAbandoned(next: readonly PendingUrlAction[]) {
  abandoned = next
  for (const l of abandonedListeners) l()
}
function subscribeAbandoned(cb: () => void): () => void {
  abandonedListeners.add(cb)
  return () => { abandonedListeners.delete(cb) }
}
function getAbandoned(): readonly PendingUrlAction[] {
  return abandoned
}
onActiveCollectionKeyChange(() => {
  if (abandoned.length) setAbandoned([])
})

// A journal-only write: the pointer, names and epoch are untouched, so no
// scope publish and no store listener. Persisted like any other write, and
// kept in memory so the next write carries it.
function persistJournal(next: StoredCollection): boolean {
  const ok = persistLocal(next)
  state = withInvariants(next)
  notifyJournal()
  return ok
}

function journalScopeIs(ownerId: string | null): boolean {
  return getActiveCollectionKey() !== null && activeCollectionOwner() === ownerId
}

/** `ownerId`'s unsettled URL actions, when that scope is active (oldest first). */
export function pendingUrlActionsFor(ownerId: string | null): readonly PendingUrlAction[] {
  if (!journalScopeIs(ownerId)) return EMPTY_ACTIONS
  return getJournal().filter(a => a.ownerId === ownerId)
}

/** One unsettled entry by its key, or null (settled, dismissed, abandoned, another scope). */
export function findUrlAction(ownerId: string | null, opKey: string): PendingUrlAction | null {
  return pendingUrlActionsFor(ownerId).find(a => a.opKey === opKey) ?? null
}

/** The journal, live, for the resume prompt. */
export function useUrlActionJournal(): readonly PendingUrlAction[] {
  return useSyncExternalStore(subscribeJournal, getJournal, getJournal)
}

/** Entries abandoned this session, for the "may have completed" notice. */
export function useAbandonedUrlActions(): readonly PendingUrlAction[] {
  return useSyncExternalStore(subscribeAbandoned, getAbandoned, getAbandoned)
}

/** The user read the abandonment notice. */
export function dismissAbandonedUrlActions(): void {
  if (abandoned.length) setAbandoned([])
}

function dropEntries(drop: (a: PendingUrlAction) => boolean): PendingUrlAction[] {
  const gone = getJournal().filter(drop)
  if (gone.length) persistJournal({ ...state, pendingUrlActions: getJournal().filter(a => !drop(a)) })
  return gone
}

/**
 * Abandon `ownerId`'s entries past the resume window: dropped from the
 * journal and reported to the "may have completed" notice. Returns how many.
 */
export function expireUrlActions(ownerId: string | null, now = Date.now()): number {
  if (!isCollectionLeader() || !journalScopeIs(ownerId)) return 0
  const gone = dropEntries(a => a.ownerId === ownerId && now - a.startedAt >= URL_ACTION_TTL_MS)
  if (gone.length) setAbandoned([...abandoned, ...gone])
  return gone.length
}

/** Abandon ONE entry the user can no longer resume (the collection moved). */
export function abandonUrlAction(ownerId: string | null, opKey: string): void {
  if (!isCollectionLeader() || !journalScopeIs(ownerId)) return
  const gone = dropEntries(a => a.ownerId === ownerId && a.opKey === opKey)
  if (gone.length) setAbandoned([...abandoned, ...gone])
}

export type { JournalResult } from './urlActionJournal.codes'

/**
 * APPEND `action` before its request is sent (a key already journaled is a
 * resume and is left as it is). Expired entries are abandoned first; past the
 * cap the action is refused ('full'), never by evicting an unsettled entry.
 * 'ok' only when it was written AND reads back from storage.
 */
export function journalUrlAction(action: PendingUrlAction): JournalResult {
  if (!isCollectionLeader() || !journalScopeIs(action.ownerId)) return JOURNAL.unstored
  const key = getActiveCollectionKey()
  if (key === null) return JOURNAL.unstored
  expireUrlActions(action.ownerId, action.startedAt)
  const list = getJournal()
  if (list.some(a => a.opKey === action.opKey)) {
    return readCollectionRecord(key).pendingUrlActions.some(a => a.opKey === action.opKey) ? JOURNAL.ok : JOURNAL.unstored
  }
  if (list.length >= URL_ACTION_CAP) return JOURNAL.full
  if (!persistJournal({ ...state, pendingUrlActions: [...list, action] })) return JOURNAL.unstored
  return readCollectionRecord(key).pendingUrlActions.some(a => a.opKey === action.opKey) ? JOURNAL.ok : JOURNAL.unstored
}

/**
 * The action under `opKey` is settled (its definitive outcome, or the user
 * dismissed it): compare-delete ONLY that entry.
 */
export function settleUrlAction(ownerId: string | null, opKey: string): void {
  if (!isCollectionLeader() || !journalScopeIs(ownerId)) return
  dropEntries(a => a.opKey === opKey)
}

/** Test seam: reset the module store between cases. */
export function __resetCollectionStore() {
  applyCollectionScopeFromAuth()
  state = loadLocal()
  setCloudPointer(null)
  // The scope store is module state too, and it now holds whatever the last
  // case published. Republishing keeps the two seams resettable together.
  publishCollectionScope({ collectionId: state.collectionId, epoch: state.epoch ?? 0 })
  emit()
}

/**
 * The live store, WITHOUT subscribing.
 *
 * The sync effect needs the current value, not the one its render closed over:
 * an import can replace the names while a pass is mid-flight, and acting on the
 * captured copy would write a version for a set that is no longer there.
 */
export function getCollectionSnapshot(): Readonly<StoredCollection> {
  return state
}

/**
 * Ask for a revalidation pass without changing any stored value.
 *
 * Bumping the revision alone is enough: CollectionSync depends on it, while
 * `getSnapshot` still returns the identical object so no consumer re-renders.
 * Used by the focus/visibility trigger, where nothing has actually changed
 * locally but the SERVER may have moved.
 */
export function requestCollectionRevalidate() {
  // syncGeneration, NOT revision: CollectionSync subscribes to the former, so
  // bumping only `revision` made focus/visibility revalidation a no-op - the
  // probe never ran and a collection imported on another device never arrived.
  emitInvalidation()
}

/**
 * The store revision. CollectionSync depends on this so a local invalidation
 * (an import rewriting the names) re-runs the sync in the SAME session, rather
 * than waiting for a pointer change that an in-place re-import never makes.
 */
export function useCollectionRevision(): number {
  return useSyncExternalStore(subscribe, getRevision, getRevision)
}

/**
 * The INVALIDATION generation. CollectionSync depends on this, not on
 * `revision`: depending on every change meant the sync's own commit re-keyed
 * it, and an unversioned cache then refetched, committed, and re-keyed forever.
 */
export function useCollectionSyncGeneration(): number {
  return useSyncExternalStore(subscribe, getSyncGeneration, getSyncGeneration)
}

/** Non-hook read of the invalidation generation, for mid-pass comparison. */
export function getCollectionSyncGeneration(): number {
  return syncGeneration
}

/**
 * Drop the collection synchronously.
 *
 * Account deletion removes the localStorage key directly and then AWAITS
 * sign-out. Without a synchronous store clear, an in-flight sync can land in
 * that window, pass its pointer check, and write the deleted collection back.
 */
export function getCollectionPointerGeneration(): number {
  return pointerGeneration
}

/**
 * Drop ONE account's cached collection (account deletion, after the server
 * confirmed it). Synchronous, and the in-memory store is cleared too when that
 * account is the active scope: an owned-name sync still in flight must not
 * land during the awaited sign-out and write the deleted collection back.
 */
export function clearAccountCollectionCache(userId: string): boolean {
  const key = collectionKeyFor(userId)
  // Verified: true only when the key is gone (or holds no collection).
  const removed = removeCollectionRecord(key)
  if (getActiveCollectionKey() !== key) return removed
  bumpCollectionClearGeneration()
  noteCollectionIdentityChange()
  state = EMPTY
  setCloudPointer(null)
  // Published from the in-memory clear: a removal that threw must not leave
  // every scoped gallery querying the collection this just deleted.
  publishCollectionScope({ collectionId: null, epoch: 0 })
  pointerGeneration += 1
  emitInvalidation()
  return removed
}

/**
 * The server refused a write to `collectionId` with 403: it belongs to
 * another account. Drop it from THIS scope's cache (local only; nothing in
 * any account is touched) so the page stops offering to write to it. True
 * when it was the active pointer and is now gone.
 */
export function dropForbiddenCollection(collectionId: string, ownerId: string | null): boolean {
  if (!isCollectionLeader() || getActiveCollectionKey() !== collectionKeyFor(ownerId)) return false
  if (state.collectionId !== collectionId) return false
  // Before any await: the clear below is ordered after every pointer write
  // already queued, and refused if a Remove succeeds meanwhile.
  const seq = pointerWriteSeq()
  bumpCollectionClearGeneration()
  setCloudPointer(null)
  // Only the POINTER goes. The cached names, count and source stay as a
  // local, never-uploaded collection (no id, names unbound), which is exactly
  // what "import into your collection" needs: the next import mints the
  // user's own row from them (the self-heal path). Unversioned, and the
  // per-copy snapshot is dropped with the foreign collection it described.
  // The in-memory pointer goes whatever storage says (nothing may write to
  // the foreign collection again this session); `durable` decides the report.
  const durable = setState({
    ...state,
    collectionId: null,
    ownedCardsCollectionId: null,
    version: null,
    proxyNames: [],
    copiesUnavailable: false,
    ownedCardsUserId: null,
    proxyNamesKnown: false,
    // An ACCOUNT's null pointer is authoritative: pending until the account
    // row no longer names the foreign collection, so the next load's
    // reconcile keeps the null (and retries the clear) instead of adopting
    // the foreign row back. A guest has no row.
    pointerPending: ownerId !== null,
    // Provenance of that null: the reconcile compare-deletes only THIS id.
    pendingNullOf: ownerId !== null ? collectionId : null,
  }, true)
  if (ownerId !== null) {
    void writePointer(ownerId, seq, () => clearSavedCollectionPointer(ownerId, collectionId)).then((r) => {
      if (r.ok && r.value) confirmAccountPointer(ownerId, null)
    })
  }
  // Reported only when the local clear survives a reload.
  return durable
}

/**
 * Delete ONE user's cloud collection, the one they confirmed, and VERIFY it is
 * gone. `deleted` only when no saved_collections row for that user holds
 * `collectionId` AND the legacy user_metadata pointer is cleared - that
 * metadata is a migration source for hydration when the row is missing, so
 * leaving `collection_id` there brought a removed collection back.
 *
 * Pinned to `userId` throughout: the row is read and deleted on a client
 * carrying THIS user's token when one can be built, filtered by user AND
 * collection, the metadata clear carries the same token, and the live session
 * is re-checked around every step. A row holding a different collection (the
 * account was re-imported on another device) is never deleted.
 *
 * Supabase does not throw on a failed or RLS-filtered delete (an `error`, or
 * zero rows), so success is decided by reading the row back.
 */
async function deleteCloudCollection(
  userId: string,
  collectionId: string,
  accessToken: string,
  // True when this device KNEW the collection was in the account (its pointer
  // was confirmed in saved_collections). Only then is a row holding some other
  // collection a "changed on another device" conflict; otherwise that row was
  // never this collection's and there is nothing to delete.
  knownInAccount: boolean,
): Promise<CloudDeleteOutcome> {
  if (!supabase) return CLOUD_DELETE.deleted
  const pinned = supabaseForToken(accessToken) ?? supabase
  const stillSame = async () => {
    const live = await readSessionIdentity()
    return live.ok && live.userId === userId
  }
  // The row this user's account holds, if any: undefined when the read failed.
  const readRow = async (): Promise<{ collection_id: string | null } | null | undefined> => {
    const { data: rows, error } = await pinned
      // eslint-disable-next-line i18next/no-literal-string -- table name, never shown
      .from('saved_collections')
      // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
      .select('collection_id')
      // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
      .eq('user_id', userId)
      .limit(1)
    if (error) return undefined
    return rows && rows.length > 0 ? (rows[0] as { collection_id: string | null }) : null
  }
  // Once a delete has been ATTEMPTED, a switch before the removal is fully
  // confirmed (row read back absent AND metadata cleared) is `incomplete`,
  // never a silent success: the row may survive an RLS-filtered delete, or the
  // legacy metadata may rehydrate it on the next login.
  let attempted = false
  const changed = (): CloudDeleteOutcome => (attempted ? CLOUD_DELETE.incomplete : CLOUD_DELETE.identityBefore)
  try {
    if (!(await stillSame())) return changed()
    const before = await readRow()
    if (before === undefined) return CLOUD_DELETE.failed
    if (!(await stillSame())) return changed()
    if (before !== null && before.collection_id !== collectionId) {
      // Pre-verified: only the collection that was confirmed is ever deleted.
      return knownInAccount ? CLOUD_DELETE.collectionChanged : CLOUD_DELETE.notInAccount
    }
    if (before === null && !knownInAccount) {
      // Never reached saved_collections, and there is no row. The only cloud
      // trace that could bring it back is a legacy user_metadata pointer: read
      // it (as this user) and clear it ONLY if it is this collection. Any
      // other pointer is an older collection of the account and must survive.
      const legacy = await readLegacyCollectionIdAs(accessToken)
      if (!legacy.ok) return CLOUD_DELETE.metadataUnknown
      if (!(await stillSame())) return changed()
      if (legacy.collectionId !== collectionId) return CLOUD_DELETE.notInAccount
    }
    if (before !== null) {
      attempted = true
      const { error } = await pinned
        // eslint-disable-next-line i18next/no-literal-string -- table name, never shown
        .from('saved_collections')
        .delete()
        // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
        .eq('user_id', userId)
        // eslint-disable-next-line i18next/no-literal-string -- column name, never shown
        .eq('collection_id', collectionId)
        .select()
      if (error) return CLOUD_DELETE.failed
      if (!(await stillSame())) return changed()
      // (a) Read back: the confirmed row must be gone.
      const after = await readRow()
      if (after === undefined) return CLOUD_DELETE.failed
      if (after !== null && after.collection_id === collectionId) return CLOUD_DELETE.failed
    }
    // (b) The legacy metadata clear, with THIS user's token (Auth API
    // directly), never through the shared client, which acts as whoever is
    // signed in by the time it runs. Re-checked first so a switch already
    // visible stops it before any request.
    attempted = true
    if (!(await stillSame())) return changed()
    if (!(await clearLegacyCollectionMetadataAs(accessToken))) return CLOUD_DELETE.failed
    // Both confirmed: the removal is DONE for this account. A switch now
    // leaves nothing undone in the cloud.
    return (await stillSame()) ? CLOUD_DELETE.deleted : CLOUD_DELETE.identityAfter
  } catch {
    return CLOUD_DELETE.failed
  }
}

/**
 * True when the cached names actually belong to the pointer in use.
 *
 * Exported so consumers and the sync agree on one rule. Null === null holds
 * deliberately: a purely local, never-uploaded collection has no pointer and no
 * binding, and its names are still the user's own.
 */
export function namesMatchPointer(c: {
  collectionId: string | null
  ownedCardsCollectionId: string | null
}): boolean {
  return c.ownedCardsCollectionId === c.collectionId
}

export function useCollection(): CollectionState {
  const collection = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const cloud = useSyncExternalStore(subscribeCloud, getCloudPointer, getCloudPointer)
  const scopeKey = useSyncExternalStore(subscribe, getActiveCollectionKey, getActiveCollectionKey)
  const owner = ownerOfCollectionKey(scopeKey)

  const saveCollection = useCallback(async (
    data: SaveCollectionInput,
  ): Promise<SaveCollectionResult> => {
    // Only the writer tab writes collections, and only into a known scope.
    // A follower tab's controls are disabled; this is the backstop.
    const key = getActiveCollectionKey()
    if (!isCollectionLeader() || key === null) return { cloudOk: false, localOk: false }
    // The account this save is FOR, from the scope it writes into. Captured
    // before any await, so a switch mid-save cannot redirect the cloud write.
    const ownerId = ownerOfCollectionKey(key)
    // Synchronously, before any await: the operation's place in pointer order.
    const opSeq = data.pointerSeq ?? pointerWriteSeq()
    const prev = state
    // The latest persisted blob, not this instance's captured state: another
    // useCollection instance on the same page may have saved since this one
    // rendered, and two content-changing saves in one tick must still get
    // strictly increasing epochs.
    const latest = loadLocal()

    // Writing names REBINDS them and RESETS the version. Doing it here rather
    // than at each call site is deliberate: there are nine mutation sites
    // (file upload, URL, Archidekt, refresh, three salvage paths, re-import,
    // clear) and one of them forgetting to invalidate would leave a trusted
    // version standing over replaced names - the exact bug class this fixes.
    // The default version is null (= provisional client parse, resync me); a
    // caller that fetched the canonical set passes the version it fetched.
    let version = prev.version
    let ownedCardsCollectionId = prev.ownedCardsCollectionId
    // C4: the proxy set, the diverged flag and the user binding are part of
    // the SAME snapshot as the names. Writing names replaces all of them
    // together (an import's parse carries none), so a proxy set can never
    // outlive the names it was fetched with.
    let proxyNames = prev.proxyNames
    let copiesUnavailable = prev.copiesUnavailable
    let ownedCardsUserId = prev.ownedCardsUserId
    let proxyNamesKnown = prev.proxyNamesKnown
    if ('ownedCards' in data) {
      ownedCardsCollectionId =
        data.ownedCardsCollectionId ?? data.collectionId ?? prev.collectionId
      version = data.version ?? null
      proxyNames = data.proxyNames ?? []
      copiesUnavailable = data.copiesUnavailable ?? false
      ownedCardsUserId = data.ownedCardsUserId ?? null
      proxyNamesKnown = data.proxyNamesKnown ?? false
    } else if ('version' in data) {
      version = data.version ?? null
    }

    // Stamp only on a GENUINE change, not on every call that merely mentions a
    // pointer/list field. CollectionSync's version-null canonical resync calls
    // saveCollection with `ownedCards` on every pass even when the server
    // returned byte-identical names - bumping there would refetch every
    // gallery keyed on the epoch (Task 7) for nothing.
    const pointerChanged = data.collectionId != null && data.collectionId !== latest.collectionId
    const listChanged = data.ownedCards != null && !sameNames(latest.ownedCards, data.ownedCards)
    // Derived from the LARGER of the latest-persisted and this instance's
    // in-memory epoch: a previous save whose localStorage write threw (quota,
    // private mode) still advances `state` without persisting, so `latest`
    // alone can under-report the true epoch and this save would otherwise
    // regress it on disk.
    const epochFloor = Math.max(latest.epoch ?? 0, prev.epoch ?? 0)

    const updated: StoredCollection = {
      collectionId: data.collectionId ?? prev.collectionId,
      ownedCards: data.ownedCards ?? prev.ownedCards,
      cardCount: data.cardCount ?? prev.cardCount,
      sourceUrl: data.sourceUrl ?? prev.sourceUrl,
      version,
      ownedCardsCollectionId,
      proxyNames,
      copiesUnavailable,
      ownedCardsUserId,
      proxyNamesKnown,
      // An account pointer written here is pending until its upsert below is
      // confirmed: a reload in between must not let the server row win.
      pointerPending: ownerId && supabase && ('collectionId' in data || 'cardCount' in data || 'sourceUrl' in data)
        ? true
        : prev.pointerPending,
      previousCollection: prev.previousCollection,
      pendingMint: prev.pendingMint,
      pendingClaim: prev.pendingClaim,
      pendingUrlActions: prev.pendingUrlActions,
      pendingNullOf: prev.pendingNullOf,
      // Stamped whenever the CONTENTS actually changed (a new pointer, a
      // genuinely different owned list, or an explicit contentChanged from a
      // caller that knows something changed the string comparison can't see -
      // e.g. a printings-only re-import). Galleries fold it into their query
      // keys, so a re-import into the same collection id still refetches the
      // owned art - but a no-op resave does not.
      epoch: pointerChanged || listChanged || data.contentChanged ? nextEpoch(epochFloor) : epochFloor,
    }

    // Local-first: local state is written before (and regardless of) any cloud
    // sync, so callers keep working offline / signed-out exactly as before.
    // Only an IMPORT invalidates. A canonical commit never does, whatever its
    // version turned out to be.
    const invalidates = ('ownedCards' in data && data.origin !== 'canonical')
    // An import (or a caller that knows the contents changed) replaced what
    // this collection IS: per-copy writes in flight against it are fenced. A
    // canonical names commit is not one, even when it moves the epoch.
    // A canonical commit that observed a NEW version of the same collection
    // that this tab's own confirmed per-copy writes cannot explain: another
    // device replaced it (a re-import), so a per-copy write still awaiting
    // confirmation here targets contents that are gone. Our own post-write
    // refresh is explained and does not count. Each own change is consumed by
    // the first canonical commit that read after it.
    let remoteReplacement = false
    if (data.origin === 'canonical' && 'version' in data) {
      const target = data.ownedCardsCollectionId ?? prev.collectionId
      const sameCollection = target != null && target === prev.collectionId && prev.ownedCardsCollectionId === target
      const explained = target != null && data.syncStamp !== undefined && consumeOwnCopiesChanges(target, data.syncStamp)
      remoteReplacement = sameCollection && prev.version !== null && data.version !== prev.version && !explained
    }
    const localOk = setState(updated, invalidates, pointerChanged || invalidates || !!data.contentChanged || remoteReplacement)

    // Persist to saved_collections table if logged in. Inspect the upsert result
    // (Supabase does not throw on error) and report it via cloudOk instead of
    // throwing, so existing local-first callers are unaffected. No cloud pointer
    // to update (signed-out / no supabase) reports cloudOk:true.
    const touchesPointer = 'collectionId' in data || 'cardCount' in data || 'sourceUrl' in data
    if (ownerId && supabase && touchesPointer) {
      // Through the ONE pointer writer: serialized against every other pointer
      // write and against Remove, and refused (no request) if a Remove for this
      // user succeeded after this operation started. The upsert itself is
      // sent only as `ownerId`, with that session's own token, and is dropped
      // if the session changed hands before it finished (savedCollections).
      const result = await writePointer(ownerId, opSeq, () => upsertSavedCollection(ownerId, {
        collection_id: updated.collectionId,
        card_count: updated.cardCount,
        source_url: updated.sourceUrl,
      }))

      // owned_cards used to be stored in user_metadata, but that bloats the
      // JWT (93KB+ for large collections) causing iOS URLSession to silently
      // drop the Authorization header. Collection data lives in the
      // saved_collections table + localStorage instead.
      // Either way, the confirmed-pointer display is touched only while this
      // save's account is still the active scope: a late completion for A
      // must not overwrite (or clear) what B has confirmed since.
      const stillOwner = activeCollectionOwner() === ownerId
      if (!result.ok || !result.value) {
        console.warn('[useCollection] saved_collections upsert failed or was dropped')
        if (stillOwner) setCloudPointer(null)
        return { cloudOk: false, localOk }
      }
      if (stillOwner) confirmAccountPointer(ownerId, updated.collectionId)
    }
    return { cloudOk: true, localOk }
  }, [])

  const clearCollection = useCallback(async (intent: RemovalIntent): Promise<ClearCollectionResult> => {
    // ONE removal, exclusive in the pointer writer: it waits for any pointer
    // write already queued or in flight, then deletes LAST; a write from an
    // operation that started before it is refused once it succeeds.
    //
    // It acts for the account that CONFIRMED (`intent`), never whoever is
    // signed in by the time it reaches the front of the queue: both the shared
    // auth snapshot and the live session must still be intent.userId before
    // any request, and the delete is scoped to that user AND the confirmed
    // collection.
    type Outcome = { ok: boolean; userId: string | null; result: ClearCollectionResult }
    // eslint-disable-next-line i18next/no-literal-string -- result code, never shown
    const IDENTITY: ClearCollectionResult = { ok: false, reason: 'identity_changed' }
    // Exclusive with the pointer lease, atomically: no await between this
    // check and removeExclusively marking the removal in progress (which
    // tryAcquirePointerLease refuses on). A guest claim or adopt holding the
    // lease could otherwise re-adopt a collection this removal just deleted.
    // eslint-disable-next-line i18next/no-literal-string -- result code, never shown
    if (pointerLeaseActive()) return { ok: false, reason: 'syncing' }
    let outcome: Outcome
    try {
      outcome = await removeExclusively<Outcome>(async () => {
        // Only the writer tab removes (a follower's Remove is disabled).
        if (!isCollectionLeader()) return { ok: false, userId: null, result: { ok: false } }
        // Per-copy writes (plan 2026-10-16 C3): removeExclusively already
        // refuses new ones (pointerRemovalInProgress); abort the ones in
        // flight and let them end before anything is deleted.
        if (await settleCopiesWritesForRemoval() === COPIES_TIMED_OUT) {
          // A per-copy write is still in the air: deleting now could race it.
          // Nothing was touched; the user can retry.
          // eslint-disable-next-line i18next/no-literal-string -- result code, never shown
          return { ok: false, userId: null, result: { ok: false, reason: 'unconfirmed' } }
        }
        const snapshot = getSupabaseIdentity()
        const snapshotOwner = snapshot.userId && !snapshot.anonymous ? snapshot.userId : null
        if (snapshot.loading || snapshotOwner !== intent.userId) {
          return { ok: false, userId: null, result: IDENTITY }
        }
        const live = await readSessionIdentity()
        // Unknown: cannot prove who this is. Retryable, nothing touched.
        if (!live.ok) return { ok: false, userId: null, result: { ok: false } }
        if (live.userId !== intent.userId) return { ok: false, userId: null, result: IDENTITY }
        // Confirmed signed out: this device only. Likewise a collection that was
        // never uploaded (no pointer): the account's row, if any, is some
        // other collection and is not touched.
        if (intent.userId === null || intent.collectionId === null) {
          // Signed in: still tombstone the account, so a pointer save that
          // started before or during this removal cannot upload it afterwards.
          return { ok: true, userId: intent.userId, result: { ok: true } }
        }
        if (!live.token) return { ok: false, userId: null, result: { ok: false } }
        const cloud = await deleteCloudCollection(intent.userId, intent.collectionId, live.token, intent.inAccount)
        switch (cloud) {
          case CLOUD_DELETE.deleted:
            return { ok: true, userId: intent.userId, result: { ok: true } }
          case CLOUD_DELETE.notInAccount:
            // Never reached the account: nothing in the cloud to remove. The
            // same tombstone as a normal removal, so a claim or pointer save
            // queued meanwhile (e.g. during the legacy read) cannot upload it.
            return { ok: true, userId: intent.userId, result: { ok: true } }
          case CLOUD_DELETE.identityAfter:
            // FULLY removed for the confirming account (tombstone it), but the
            // browser now belongs to someone else: leave its state alone.
            return { ok: true, userId: intent.userId, result: IDENTITY }
          case CLOUD_DELETE.metadataUnknown:
            // Could not tell whether the legacy pointer is this collection.
            // Nothing changed; retryable (the account did not change, so this
            // is a connection problem, not a sign-in one).
            // eslint-disable-next-line i18next/no-literal-string -- result code, never shown
            return { ok: false, userId: null, result: { ok: false, reason: 'unconfirmed' } }
          case CLOUD_DELETE.incomplete:
            // A delete was attempted; tombstone the confirming account so a
            // stale write cannot recreate it, but report that the removal is
            // NOT confirmed. Nothing local is touched.
            // eslint-disable-next-line i18next/no-literal-string -- result code, never shown
            return { ok: true, userId: intent.userId, result: { ok: false, reason: 'incomplete' } }
          case CLOUD_DELETE.identityBefore:
            return { ok: false, userId: null, result: IDENTITY }
          case CLOUD_DELETE.collectionChanged:
            // eslint-disable-next-line i18next/no-literal-string -- result code, never shown
            return { ok: false, userId: null, result: { ok: false, reason: 'collection_changed' } }
          default:
            return { ok: false, userId: null, result: { ok: false } }
        }
      })
    } catch {
      return { ok: false }
    }
    // Still the confirming account's scope? A Remove for A that finishes
    // after a switch to B must not touch B's in-memory state (the pointer
    // writer's tombstone stays scoped to A either way).
    const sameScope = getActiveCollectionKey() === collectionKeyFor(intent.userId)
    if (outcome.ok && sameScope) {
      // The cloud removal happened. NOW fence in-flight imports (only a
      // successful removal does: a failed one must not discard concurrent
      // work).
      bumpCollectionClearGeneration()
      setCloudPointer(null)
    }
    if (!outcome.ok || !outcome.result.ok) return outcome.result

    // Durable first, verified, THEN in memory. Reporting success while the
    // stored record survives would bring the collection back on the next load.
    // The key is the CONFIRMING scope's, never whatever is active by now.
    // The account's live way back (previousCollection) is NOT the collection
    // the user removed: it survives, and the empty page still offers it.
    const confirmingKey = collectionKeyFor(intent.userId)
    const slot = intent.userId ? livePreviousCollection(readCollectionRecord(confirmingKey)) : null
    // Never the collection being removed: Switch back would re-point the
    // account at what the user just removed.
    const keepPrevious = slot && slot.collectionId !== intent.collectionId ? slot : null
    // Durable and conditional (r3 blocker 2): only while the record is still
    // the removed collection (or its pointer was already cleared above).
    const removedId = intent.collectionId
    const removal = await removeCollectionRecordDurable(
      confirmingKey,
      rec => rec.collectionId === null || rec.collectionId === removedId,
      { keepPrevious },
    )
    // eslint-disable-next-line i18next/no-literal-string -- result code, never shown
    if (removal === DURABLE_REMOVAL.failed) return { ok: false, reason: 'local_clear_failed' }
    // Re-checked AFTER the await: still the confirming scope, and its memory
    // not holding a REPLACEMENT collection. Otherwise nothing in memory is
    // touched (setState persists to whatever scope is active NOW).
    const stillThisScope = getActiveCollectionKey() === confirmingKey
      && (state.collectionId === null || state.collectionId === removedId)
    // setState publishes the emptied pointer and epoch to the scope store.
    if (removal === DURABLE_REMOVAL.removed && sameScope && stillThisScope) setState({ ...EMPTY, previousCollection: keepPrevious })
    // Kept per-copy Retry intents of the removed collection go with it.
    if (intent.userId && intent.collectionId) clearRetryIntents({ userId: intent.userId, collectionId: intent.collectionId })
    // So do its owed "log what you pulled" prompts.
    if (intent.userId && intent.collectionId) clearAllPendingPulls(intent.userId, intent.collectionId)
    return { ok: true }
  }, [])

  return {
    collectionId: collection.collectionId,
    // Names are exposed ONLY when they belong to the pointer in use. A failed
    // snapshot fetch correctly leaves an older collection's cache in place, and
    // without this every consumer would go on reporting THOSE cards as owned
    // for whatever collection the pointer now names.
    ownedCards: namesMatchPointer(collection) ? collection.ownedCards : null,
    cardCount: collection.cardCount,
    sourceUrl: collection.sourceUrl,
    version: collection.version,
    ownedCardsCollectionId: collection.ownedCardsCollectionId,
    proxyNames: namesMatchPointer(collection) ? collection.proxyNames : [],
    copiesUnavailable: collection.copiesUnavailable,
    ownedCardsUserId: collection.ownedCardsUserId,
    proxyNamesKnown: collection.proxyNamesKnown,
    previousCollection: livePreviousCollection(collection),
    epoch: collection.epoch,
    cloudSynced: !!owner && cloud != null && cloud.userId === owner
      && collection.collectionId != null && cloud.collectionId === collection.collectionId,
    // The account pointer is loaded by the writer tab's sign-in reconcile
    // (CollectionSync), not per hook instance, so there is nothing to wait on.
    loading: false,
    saveCollection,
    clearCollection,
  }
}
